/**
 * Cross-tab session-event hub. Five session-lifecycle events fan
 * out to every same-origin tab the same browser has open so a
 * logout / session-revoke / new-login in any one tab converges
 * the rest:
 *
 *   - `login_succeeded`        — a fresh login landed in this tab
 *   - `logout_explicit`        — user clicked "log out" here
 *   - `session_token_issued`   — csrf token rotated (future hook;
 *                                 the BE issues exactly once today
 *                                 but the wire shape is ready)
 *   - `session_revoked`        — the BE revoked the session
 *                                 (server-initiated, account-wide)
 *   - `displaced`              — per-table singleton-slot displace
 *
 * Transport-tier defaults to `BroadcastChannel` (W3C HTML Living
 * Standard §9.4) and falls back to a `storage` event on the same-
 * origin `localStorage` slot when the API is missing — primarily
 * Safari ≤15.4 + a handful of mobile webkit forks. Both subscribe
 * paths run side-by-side; the publish path picks one based on
 * availability so a working `BroadcastChannel` doesn't fan a
 * duplicate `storage` event. A per-tab `sender_tab_id`
 * (`crypto.randomUUID()`) lets the receiver short-circuit its
 * own echo without an explicit `removeListener`-before-publish
 * dance — the filter runs in the handler.
 *
 * Mount-once invariant — receiving a `session_revoked` /
 * `displaced` / `logout_explicit` event from a sibling tab
 * funnels through the same `auth_store.markSessionRevoked(reason)`
 * action that the WS + 401 paths already use, so the existing
 * idempotency guard handles dedup. No new mount-once machinery
 * needed at this layer; the hub is pure pub/sub.
 */

const CHANNEL_NAME = "arkhe-holdem-session";
const STORAGE_KEY = "arkhe.cross_tab.session";

/** Per-tab identity stamp — survives the lifetime of the React
 *  app instance + lets receiving tabs filter out the publisher's
 *  own echo. Lazily initialised the first time a publisher /
 *  subscriber binds so SSR boot doesn't crash on a missing
 *  `crypto` global. */
let cachedTabId: string | null = null;

function tabId(): string {
  if (cachedTabId !== null) return cachedTabId;
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    cachedTabId = crypto.randomUUID();
  } else {
    // Pre-randomUUID fallback. Combines a high-resolution monotonic
    // clock with a 36-base random suffix — collision probability
    // across a single browser's tab fleet is negligible.
    cachedTabId = `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
  return cachedTabId;
}

/** Wire shape of every cross-tab fan-out event. The
 *  discriminated union keeps every consumer in a single
 *  switch statement; future variants extend the tail. */
export type CrossTabEvent =
  | { readonly type: "login_succeeded"; readonly playerIdHex: string }
  | { readonly type: "logout_explicit"; readonly reason: string }
  | { readonly type: "session_token_issued"; readonly csrfToken: string }
  | { readonly type: "session_revoked"; readonly reason: string }
  | { readonly type: "displaced"; readonly reason: string };

interface CrossTabEnvelope {
  readonly senderTabId: string;
  readonly event: CrossTabEvent;
}

/** Module-level channel singleton — re-using the same instance
 *  across publish + subscribe so the listeners share a queue.
 *  Lazily created so a node-only vitest run that imports the
 *  module without exercising the API skips the constructor. */
let channel: BroadcastChannel | null = null;

function getChannel(): BroadcastChannel | null {
  if (channel !== null) return channel;
  if (typeof BroadcastChannel === "undefined") return null;
  try {
    channel = new BroadcastChannel(CHANNEL_NAME);
    return channel;
  } catch {
    // Some sandboxed contexts (private browsing in certain
    // browsers) throw on construct — fall back to storage.
    return null;
  }
}

/**
 * Fan an event to every other tab. Picks the strongest
 * available transport:
 *
 *   1. `BroadcastChannel.postMessage` — preferred (every modern
 *      browser bar the Safari ≤15.4 fallback range).
 *   2. `localStorage` write + immediate remove — the StorageEvent
 *      fires on every OTHER same-origin tab. The remove fires
 *      a second StorageEvent with `newValue === null` which the
 *      subscriber filters; this keeps the slot empty so a hard
 *      reload mid-fan doesn't replay a stale payload.
 *
 * Best-effort — quota / private-browsing / sandboxed contexts
 * drop the publish silently. The cross-tab convergence is a
 * UX cue, not the source of truth; the WS + HTTP paths each
 * carry their own revoke triggers so a missed broadcast can't
 * leave the slice stuck logged-in against a revoked server
 * session.
 */
export function publishCrossTab(event: CrossTabEvent): void {
  const envelope: CrossTabEnvelope = { senderTabId: tabId(), event };
  const ch = getChannel();
  if (ch !== null) {
    try {
      ch.postMessage(envelope);
      return;
    } catch {
      // postMessage can fail in obscure sandboxed contexts —
      // fall through to the storage path.
    }
  }
  try {
    if (typeof localStorage === "undefined") return;
    const payload = JSON.stringify(envelope);
    localStorage.setItem(STORAGE_KEY, payload);
    // Immediate remove — keep the slot empty so a hard reload
    // mid-fan doesn't replay a stale envelope on the next tab's
    // mount + so the StorageEvent's `newValue` doesn't sit in
    // the DOM-readable store any longer than necessary.
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // SSR / private-browsing / quota — drop silently.
  }
}

export type CrossTabHandler = (event: CrossTabEvent) => void;

/**
 * Subscribe to remote cross-tab events. Returns an unsubscribe
 * fn the caller wires into a React `useEffect` cleanup. Self-
 * echoes (envelopes carrying our own `senderTabId`) are filtered
 * before the handler runs so the caller never sees its own
 * publish round-tripped.
 *
 * Both transport paths are subscribed concurrently — even when
 * `BroadcastChannel` is available, the `storage` event listener
 * stays mounted as defense-in-depth (a fork that flips the
 * primary mid-session keeps converging through the secondary).
 * The publish path picks one transport per event, so the dual
 * subscribe never produces a duplicate delivery on the same
 * envelope.
 */
export function subscribeCrossTab(handler: CrossTabHandler): () => void {
  const cleanups: Array<() => void> = [];
  const ownTabId = tabId();

  const ch = getChannel();
  if (ch !== null) {
    const onMessage = (e: MessageEvent): void => {
      const env = e.data as Partial<CrossTabEnvelope> | null;
      if (env === null || typeof env !== "object") return;
      if (env.senderTabId === ownTabId) return;
      if (typeof env.senderTabId !== "string") return;
      if (env.event === undefined || typeof env.event !== "object") return;
      handler(env.event as CrossTabEvent);
    };
    ch.addEventListener("message", onMessage);
    cleanups.push(() => ch.removeEventListener("message", onMessage));
  }

  if (typeof window !== "undefined") {
    const onStorage = (e: StorageEvent): void => {
      if (e.key !== STORAGE_KEY) return;
      // The publish path follows `setItem` with an immediate
      // `removeItem`; ignore the remove side (newValue === null)
      // so the handler only runs once per logical fan event.
      if (e.newValue === null) return;
      try {
        const env = JSON.parse(e.newValue) as Partial<CrossTabEnvelope> | null;
        if (env === null || typeof env !== "object") return;
        if (env.senderTabId === ownTabId) return;
        if (typeof env.senderTabId !== "string") return;
        if (env.event === undefined || typeof env.event !== "object") return;
        handler(env.event as CrossTabEvent);
      } catch {
        // Malformed payload — drop.
      }
    };
    window.addEventListener("storage", onStorage);
    cleanups.push(() => window.removeEventListener("storage", onStorage));
  }

  return () => {
    for (const fn of cleanups) fn();
  };
}

/**
 * Test seam — vitest isolates each test file in a fresh
 * module instance, but a single file that exercises both
 * paths benefits from a force-rebind helper. Production
 * call sites never hit this.
 */
export function _resetCrossTabForTest(): void {
  if (channel !== null) {
    try {
      channel.close();
    } catch {
      // Best-effort close.
    }
    channel = null;
  }
  cachedTabId = null;
}
